import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/base/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, serviceCategories, featuredEvents, trainingPrograms, featuredProducts } from '@/mocks/data';

const tabs = [
  { id: 'events', label: '体育赛事', icon: 'ri-trophy-line' },
  { id: 'training', label: '运动培训', icon: 'ri-run-line' },
  { id: 'products', label: '体育用品', icon: 'ri-shopping-bag-3-line' },
];

function ServicesSEO() {
  return (
    <SEO
      title={`服务分类 - ${siteInfo.name} | 体育赛事 运动培训 体育用品`}
      description="满冠体育提供体育赛事组织、专业运动培训、优质体育用品三大核心服务。覆盖马拉松、篮球、足球、游泳等项目，一站式满足您的体育需求。"
      keywords="体育赛事服务,运动培训机构,体育用品商城,满冠体育服务,赛事策划,专业教练培训"
      canonicalUrl={`${siteInfo.url}/services`}
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '服务分类 - 满冠体育',
        url: `${siteInfo.url}/services`,
        description: '满冠体育服务分类页面，涵盖体育赛事、运动培训和体育用品三大板块。',
        isPartOf: { '@id': `${siteInfo.url}#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
            { '@type': 'ListItem', position: 2, name: '服务分类', item: `${siteInfo.url}/services` },
          ],
        },
      }}
    />
  );
}

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabs.find((t) => t.id === searchParams.get('tab'))?.id || 'events';

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const currentCategory = serviceCategories.find((c) => c.id === activeTab);

  return (
    <Layout>
      <ServicesSEO />

      <div className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="container-custom py-8 md:py-12">
            <Breadcrumb items={[{ label: '服务分类' }]} />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
              服务分类
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              覆盖体育赛事、专业培训和优质装备三大领域，打造一站式体育服务体验
            </p>
          </div>
        </div>

        <div className="container-custom py-6">
          <div className="flex items-center gap-1 bg-background-100 p-1 rounded-full w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {currentCategory && (
          <div className="container-custom mb-10">
            <div className="bg-background-100 rounded-lg overflow-hidden mb-10">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="relative h-52 md:h-72 overflow-hidden">
                  <img
                    src={currentCategory.image}
                    alt={currentCategory.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-3">
                    {currentCategory.name}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    {currentCategory.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {currentCategory.features.map((f) => (
                      <span key={f} className="px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-md text-xs font-medium">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {activeTab === 'events' && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">热门赛事</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {featuredEvents.map((event) => (
                    <div key={event.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer">
                      <div className="relative h-44 md:h-48 overflow-hidden">
                        <img src={event.image} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 right-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            event.status === '报名中' ? 'bg-accent-500 text-background-50' :
                            event.status === '进行中' ? 'bg-secondary-500 text-background-50' :
                            'bg-foreground-700 text-background-100'
                          }`}>{event.status}</span>
                        </div>
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-2 font-heading">{event.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-600">
                          <span><i className="ri-calendar-line mr-1"></i>{event.date}</span>
                          <span><i className="ri-map-pin-line mr-1"></i>{event.location}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'training' && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">精选课程</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {trainingPrograms.map((p) => (
                    <div key={p.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer">
                      <div className="relative h-40 md:h-44 overflow-hidden">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-0.5 bg-primary-500 text-background-50 rounded-md text-xs font-medium">{p.level}</span>
                        </div>
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="text-sm md:text-base font-bold text-foreground-900 mb-1 font-heading">{p.name}</h3>
                        <p className="text-xs text-foreground-600 mb-2">{p.coach}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-primary-500">{p.price}</span>
                          <span className="text-xs text-foreground-500">{p.duration} | {p.age}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">热门商品</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  {featuredProducts.map((p) => (
                    <div key={p.id} className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer" data-product-shop>
                      <div className="relative h-36 md:h-40 overflow-hidden bg-background-200">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <p className="text-xs text-foreground-500 mb-0.5">{p.brand}</p>
                        <h3 className="text-sm font-bold text-foreground-900 mb-1 line-clamp-1">{p.name}</h3>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-primary-500">{p.price}</span>
                          <span className="text-xs text-foreground-500">
                            <i className="ri-star-fill text-accent-500 text-xs"></i> {p.rating}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}